export interface FilterOption {
  id: string
  name: string
  path: string
  kind: 'leaf' | 'group'
  parent_id: string | null
  available: boolean
  record_type?: string
}

export interface FilterFieldOptions {
  values: FilterOption[]
}

export interface FilterSelection {
  kind: string
  id: string
}

export type IssueFilterOptions = Record<string, FilterFieldOptions>
export type EventFilters = Record<string, Record<string, FilterSelection[]>>

export const FILTER_FIELDS_BY_EVENT: Record<string, string[]> = {
  'issue.created': ['categories', 'priorities', 'teams', 'statuses'],
  'issue.updated': ['categories', 'priorities', 'teams', 'statuses', 'resolutions'],
  'note.created': ['teams'],
  'note.updated': ['teams'],
  'checklist.created': ['teams'],
  'checklist.updated': ['teams', 'statuses']
}

export function isFilterable (eventType: string): boolean {
  return FILTER_FIELDS_BY_EVENT[eventType] !== undefined
}

export function fieldsFor (eventType: string): string[] {
  return FILTER_FIELDS_BY_EVENT[eventType] ?? []
}

export function optionsFor (options: IssueFilterOptions, field: string): FilterFieldOptions {
  const fieldOptions = options?.[field]
  return { values: fieldOptions?.values ?? [] }
}

export function optionsForEvent (options: IssueFilterOptions, field: string, eventType: string): FilterFieldOptions {
  const fieldOptions = optionsFor(options, field)
  const recordType = eventType.startsWith('checklist.') ? 'CHECKLIST' : eventType.startsWith('issue.') ? 'ISSUE' : undefined

  return {
    values: recordType === undefined || field !== 'statuses'
      ? fieldOptions.values
      : fieldOptions.values.filter((option) => option.record_type === recordType),
  }
}

export function optionByIdForEvent (options: IssueFilterOptions, field: string, id: string, eventType: string): FilterOption | undefined {
  return optionsForEvent(options, field, eventType).values.find((option) => option.id === id)
}

export function missingSelections (
  filters: EventFilters,
  options: IssueFilterOptions,
  eventType: string,
  field: string
): FilterSelection[] {
  return (filters[eventType]?.[field] ?? []).filter((selection) => optionByIdForEvent(options, field, selection.id, eventType) === undefined)
}

export function selectableOptions (
  filters: EventFilters,
  options: IssueFilterOptions,
  eventType: string,
  field: string
): FilterOption[] {
  const selectedIds = (filters[eventType]?.[field] ?? []).map((selection) => selection.id)
  return optionsForEvent(options, field, eventType).values.filter((option) => option.available || selectedIds.includes(option.id))
}

export function isDescendantOfSelectedGroup (
  options: IssueFilterOptions,
  field: string,
  option: FilterOption | undefined,
  selectedIds: string[]
): boolean {
  if (option === undefined) {
    return false
  }

  const optionsById = new Map(optionsFor(options, field).values.map((value) => [value.id, value]))
  let parentId = option.parent_id

  while (parentId !== null) {
    if (selectedIds.includes(parentId) && optionsById.get(parentId)?.kind === 'group') {
      return true
    }
    parentId = optionsById.get(parentId)?.parent_id ?? null
  }

  return false
}
